import { useState, useEffect } from 'react';
import axios from 'axios';
import BasketballPlayerStatsTable from './BasketballPlayerStatsTable';
import Navbar from './Navbar';

const GIST_URL = 'https://gist.githubusercontent.com/junaid-mahmood/6ff7ea03bf75af79bdd000d1a591897c/raw';

// Add axios default configuration
axios.defaults.withCredentials = false;

const formatImageName = (name) => {
  const cleanName = name.replace(' Player Profile', '').trim();
  return cleanName.split(' ')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('-');
};

const getImageUrl = (playerName) => {
  const formattedName = formatImageName(playerName);
  return `/nba/nbaheadshots/${formattedName}.jpg`;
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #3b82f6 100%)',
    backgroundSize: '200% 200%',
    color: '#ffffff',
    padding: '6rem 1.5rem 2rem',
    animation: 'gradientShift 15s ease infinite',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '2.5rem',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '2rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    textShadow: '0 0 20px rgba(96, 165, 250, 0.3)',
    animation: 'titleGlow 2s ease-in-out infinite',
  },
  comparisonSections: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '2rem',
    marginBottom: '0',
    background: 'rgba(155, 155, 155, 0.1)',
    backdropFilter: 'blur(10px)',
    padding: '2rem',
    borderRadius: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
    },
  },
  section: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    width: '100%',
    margin: '0 auto',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)',
    },
  },
  sectionTitle: {
    color: '#1a365d',
    fontSize: '1.3rem',
    marginBottom: '1rem',
    textAlign: 'center',
    fontWeight: '700',
    background: 'linear-gradient(45deg, #1a365d, #2d5a9e)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  playerSearch: {
    position: 'relative',
    width: '90%',
    marginBottom: '0.75rem',
    margin: '0 auto',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '2px solid rgba(59, 130, 246, 0.2)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    fontSize: '1rem',
    color: '#1a365d',
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)',
    },
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    maxHeight: '250px',
    overflowY: 'auto',
    zIndex: 1000,
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  searchResultItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  resultImage: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    marginRight: '1rem',
    objectFit: 'cover',
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  resultInfo: {
    flex: 1,
  },
  resultName: {
    fontWeight: '500',
    color: '#1a365d',
    fontSize: '0.875rem',
  },
  resultMeta: {
    fontSize: '0.75rem',
    color: '#4a5568',
    marginTop: '0.25rem',
  },
  loading: {
    padding: '0.75rem 1rem',
    textAlign: 'center',
    color: '#64748b',
  },
  playerCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1rem',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.1)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    },
  },
  playerImage: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '50%',
    marginRight: '0.5rem',
  },
  playerInfo: {
    flex: 1,
    textAlign: 'left',
    marginRight: '1rem',
  },
  playerName: {
    color: '#1a365d',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.1rem',
  },
  statsGrid: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.25rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '0.7rem',
    color: '#4a5568',
  },
  statValue: {
    fontSize: '0.8rem',
    color: '#1a365d',
    fontWeight: '600',
  },
  removeButton: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: '#666',
    padding: '0.1rem',
  },
  comparisonResults: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    marginTop: '2rem',
    maxWidth: '1000px',
    margin: '2rem auto',
    animation: 'slideUp 0.5s ease-out',
    border: '1px solid rgba(59, 130, 246, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    fontSize: '0.95rem',
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  th: {
    padding: '1rem',
    textAlign: 'center',
    fontWeight: '700',
    color: 'white',
    background: 'linear-gradient(45deg, #1a365d, #2d5a9e)',
    borderBottom: '3px solid rgba(255, 255, 255, 0.1)',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  td: {
    padding: '0.5rem',
    textAlign: 'center',
    color: '#1a365d',
    borderBottom: '1px solid #e2e8f0',
    borderRight: '1px solid #e2e8f0',
  },
  positiveChange: {
    color: '#059669',
    fontWeight: '700',
    backgroundColor: 'rgba(209, 250, 229, 0.8)',
    textShadow: '0 0 8px rgba(5, 150, 105, 0.2)',
  },
  negativeChange: {
    color: '#dc2626',
    fontWeight: '700',
    backgroundColor: 'rgba(254, 226, 226, 0.8)',
    textShadow: '0 0 8px rgba(220, 38, 38, 0.2)',
  },
  playersList: {
    marginTop: '1rem',
  },
  comparisonActions: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2rem',
    marginBottom: '1rem',
    width: '100%'
  },
  button: {
    background: 'linear-gradient(45deg, #1e40af, #3b82f6)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    minWidth: '200px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    '&:disabled': {
      background: 'linear-gradient(45deg, #cbd5e1, #94a3b8)',
      cursor: 'not-allowed',
      opacity: 0.7,
    },
    '&:hover:not(:disabled)': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
      background: 'linear-gradient(45deg, #2563eb, #60a5fa)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(1px)',
    },
  },
};

const PlayerSearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);

  useEffect(() => {
    const fetchAllPlayers = async () => {
      try {
        const response = await axios.get(GIST_URL);
        const players = response.data.players;
        setAllPlayers(players);
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };
    fetchAllPlayers();
  }, []);

  const searchPlayers = async (searchQuery) => {
    setLoading(true);
    try {
      let filteredPlayers = allPlayers;
      if (searchQuery.trim()) {
        filteredPlayers = allPlayers.filter(player => {
          const playerName = player.name.toLowerCase().replace(' Player Profile', '');
          const searchTerm = searchQuery.toLowerCase();
          return playerName.includes(searchTerm);
        });
      }
      setResults(filteredPlayers.slice(0, 10));
    } catch (error) {
      console.error('Error searching players:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFocused) {
      searchPlayers('');
    }
  }, [isFocused, allPlayers]);

  return (
    <div style={styles.playerSearch}>
      <input
        type="text"
        placeholder="Search players..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          searchPlayers(e.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        style={styles.searchInput}
      />
      {(loading || (isFocused && results.length > 0)) && (
        <div style={styles.searchResults}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            results.map((player) => (
              <div
                key={player.name}
                style={styles.searchResultItem}
                onClick={() => {
                  onSelect(player);
                  setQuery('');
                  setResults([]);
                  setIsFocused(false);
                }}
              >
                <img
                  src={getImageUrl(player.name)}
                  alt={player.name}
                  style={styles.resultImage}
                  onError={(e) => {
                    e.target.src = '/nba/nbaheadshots/default.jpg';
                  }}
                />
                <div style={styles.resultInfo}>
                  <div style={styles.resultName}>{player.name}</div>
                  <div style={styles.resultMeta}>
                    {player.team} • {player.position}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const PlayerCard = ({ player, onRemove }) => {
  const [imageError, setImageError] = useState(false);
  
  if (!player) return null;

  const displayName = player.name.replace(' Player Profile', '');
  const imageUrl = getImageUrl(player.name);

  return (
    <div style={styles.playerCard}>
      <img
        src={imageError ? '/nba/nbaheadshots/default.jpg' : imageUrl}
        alt={displayName}
        style={styles.playerImage}
        onError={() => setImageError(true)}
      />
      <div style={styles.playerInfo}>
        <h3 style={styles.playerName}>{displayName}</h3>
        <div style={{ fontSize: '0.75rem', color: '#4a5568' }}>
          {player.team} • {player.position || 'N/A'}
        </div>
        <div style={styles.statsGrid}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>PPG:</span>
            <span style={styles.statValue}>{player.ppg}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>RPG:</span>
            <span style={styles.statValue}>{player.rpg}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>APG:</span>
            <span style={styles.statValue}>{player.apg}</span>
          </div>
        </div>
      </div>
      <button style={styles.removeButton} onClick={onRemove}>×</button>
    </div>
  );
};

const BasketballPlayerComparison = () => {
  const [activeTab, setActiveTab] = useState('comparison');
  const [tradingPlayers, setTradingPlayers] = useState([]);
  const [receivingPlayers, setReceivingPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const addPlayer = (type, player) => {
    if (type === 'trading' && tradingPlayers.length < 3) {
        setTradingPlayers(prev => [...prev, { ...player, id: Date.now() }]);
    } else if (type === 'receiving' && receivingPlayers.length < 3) {
        setReceivingPlayers(prev => [...prev, { ...player, id: Date.now() }]);
    }
  };

  const removePlayer = (type, playerId) => {
    if (type === 'trading') {
      setTradingPlayers(prev => prev.filter(p => p.id !== playerId));
    } else {
      setReceivingPlayers(prev => prev.filter(p => p.id !== playerId));
    }
  };

  const comparePlayers = () => {
    setLoading(true);
    try {
      const calculateAverages = (playerGroup) => {
        if (!playerGroup || playerGroup.length === 0) {
          return {
            ppg: "0.0",
            rpg: "0.0",
            apg: "0.0",
            fgPercentage: "0.0",
            threePtPercentage: "0.0"
          };
        }

        const total = playerGroup.reduce((acc, player) => ({
          ppg: acc.ppg + (parseFloat(player.ppg) || 0),
          rpg: acc.rpg + (parseFloat(player.rpg) || 0),
          apg: acc.apg + (parseFloat(player.apg) || 0),
          fgPercentage: acc.fgPercentage + (parseFloat(player.fgPercentage) || 0),
          threePtPercentage: acc.threePtPercentage + (parseFloat(player.threePtPercentage) || 0)
        }), { ppg: 0, rpg: 0, apg: 0, fgPercentage: 0, threePtPercentage: 0 });

        const count = playerGroup.length;
        return {
          ppg: (total.ppg / count).toFixed(1),
          rpg: (total.rpg / count).toFixed(1),
          apg: (total.apg / count).toFixed(1),
          fgPercentage: (total.fgPercentage / count).toFixed(1),
          threePtPercentage: (total.threePtPercentage / count).toFixed(1)
        };
      };

      const tradingStats = calculateAverages(tradingPlayers);
      const receivingStats = calculateAverages(receivingPlayers);

      const analysis = {
        ppg: (parseFloat(receivingStats.ppg) - parseFloat(tradingStats.ppg)).toFixed(1),
        rpg: (parseFloat(receivingStats.rpg) - parseFloat(tradingStats.rpg)).toFixed(1),
        apg: (parseFloat(receivingStats.apg) - parseFloat(tradingStats.apg)).toFixed(1),
        fgPercentage: (parseFloat(receivingStats.fgPercentage) - parseFloat(tradingStats.fgPercentage)).toFixed(1),
        threePtPercentage: (parseFloat(receivingStats.threePtPercentage) - parseFloat(tradingStats.threePtPercentage)).toFixed(1)
      };

      setComparisonResults({ tradingStats, receivingStats, analysis });
      setShowResults(true);
    } catch (error) {
      console.error('Error comparing players:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar isLoggedIn={isLoggedIn} currentPage="compare" />
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Basketball Trade Analysis</h1>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <button
          style={{
            ...styles.button,
            backgroundColor: activeTab === 'comparison' ? '#2d5a9e' : '#1a365d',
            minWidth: '150px'
          }}
          onClick={() => setActiveTab('comparison')}
        >
          Compare Players
        </button>
        <button
          style={{
            ...styles.button,
            backgroundColor: activeTab === 'stats' ? '#2d5a9e' : '#1a365d',
            minWidth: '150px'
          }}
          onClick={() => setActiveTab('stats')}
        >
          View Stats
        </button>
      </div>

      {activeTab === 'comparison' ? (
        <>
          <div style={styles.comparisonSections}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Players to Trade</h3>
              <PlayerSearch onSelect={(player) => addPlayer('trading', player)} />
              <div style={styles.playersList}>
                {tradingPlayers.map((player) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onRemove={() => removePlayer('trading', player.id)}
                  />
                ))}
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Players to Receive</h3>
              <PlayerSearch onSelect={(player) => addPlayer('receiving', player)} />
              <div style={styles.playersList}>
                {receivingPlayers.map((player) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onRemove={() => removePlayer('receiving', player.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div style={styles.comparisonActions}>
            <button
              style={styles.button}
              onClick={comparePlayers}
              disabled={tradingPlayers.length === 0 || receivingPlayers.length === 0 || loading}
            >
              {loading ? 'Comparing...' : 'Compare Trade'}
            </button>
          </div>

          {showResults && comparisonResults && (
            <div style={styles.comparisonResults}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Statistics</th>
                    <th style={styles.th}>Trading Away</th>
                    <th style={styles.th}>Receiving</th>
                    <th style={styles.th}>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{...styles.td, ...styles.statLabel}}>Points per Game</td>
                    <td style={styles.td}>{Number(comparisonResults.tradingStats?.ppg || 0).toFixed(1)}</td>
                    <td style={styles.td}>{Number(comparisonResults.receivingStats?.ppg || 0).toFixed(1)}</td>
                    <td style={{
                      ...styles.td,
                      ...(Number(comparisonResults.analysis?.ppg || 0) > 0 ? styles.positiveChange : styles.negativeChange)
                    }}>
                      {Number(comparisonResults.analysis?.ppg || 0) > 0 ? '+' : ''}{Number(comparisonResults.analysis?.ppg || 0).toFixed(1)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...styles.td, ...styles.statLabel}}>Rebounds per Game</td>
                    <td style={styles.td}>{Number(comparisonResults.tradingStats?.rpg || 0).toFixed(1)}</td>
                    <td style={styles.td}>{Number(comparisonResults.receivingStats?.rpg || 0).toFixed(1)}</td>
                    <td style={{
                      ...styles.td,
                      ...(Number(comparisonResults.analysis?.rpg || 0) > 0 ? styles.positiveChange : styles.negativeChange)
                    }}>
                      {Number(comparisonResults.analysis?.rpg || 0) > 0 ? '+' : ''}{Number(comparisonResults.analysis?.rpg || 0).toFixed(1)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...styles.td, ...styles.statLabel}}>Assists per Game</td>
                    <td style={styles.td}>{Number(comparisonResults.tradingStats?.apg || 0).toFixed(1)}</td>
                    <td style={styles.td}>{Number(comparisonResults.receivingStats?.apg || 0).toFixed(1)}</td>
                    <td style={{
                      ...styles.td,
                      ...(Number(comparisonResults.analysis?.apg || 0) > 0 ? styles.positiveChange : styles.negativeChange)
                    }}>
                      {Number(comparisonResults.analysis?.apg || 0) > 0 ? '+' : ''}{Number(comparisonResults.analysis?.apg || 0).toFixed(1)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{...styles.td, ...styles.statLabel}}>FG Percentage</td>
                    <td style={styles.td}>{Number(comparisonResults.tradingStats?.fgPercentage || 0).toFixed(1)}%</td>
                    <td style={styles.td}>{Number(comparisonResults.receivingStats?.fgPercentage || 0).toFixed(1)}%</td>
                    <td style={{
                      ...styles.td,
                      ...(Number(comparisonResults.analysis?.fgPercentage || 0) > 0 ? styles.positiveChange : styles.negativeChange)
                    }}>
                      {Number(comparisonResults.analysis?.fgPercentage || 0) > 0 ? '+' : ''}{Number(comparisonResults.analysis?.fgPercentage || 0).toFixed(1)}%
                    </td>
                  </tr>
                  <tr>
                    <td style={{...styles.td, ...styles.statLabel}}>3PT Percentage</td>
                    <td style={styles.td}>{Number(comparisonResults.tradingStats?.threePtPercentage || 0).toFixed(1)}%</td>
                    <td style={styles.td}>{Number(comparisonResults.receivingStats?.threePtPercentage || 0).toFixed(1)}%</td>
                    <td style={{
                      ...styles.td,
                      ...(Number(comparisonResults.analysis?.threePtPercentage || 0) > 0 ? styles.positiveChange : styles.negativeChange)
                    }}>
                      {Number(comparisonResults.analysis?.threePtPercentage || 0) > 0 ? '+' : ''}{Number(comparisonResults.analysis?.threePtPercentage || 0).toFixed(1)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <BasketballPlayerStatsTable />
      )}
    </div>
    </>
  );
};

export default BasketballPlayerComparison; 